<script setup>
/**
 * 复刻 Ionic 4 的 <ion-select>（md 模式，interface 默认 'alert'）。
 *
 * 逆向来源：web/133.a2bb13e64dd416532e81.js（styleMode = "md"）
 *   render()：[div.select-text(.select-placeholder?), div.select-icon > div.select-icon-inner, button]
 *   CSS 逐条取自该文件的 style getter
 *
 * 旧版牌九「下注记录」里的用法：
 *   <ion-item>
 *     <ion-label>结算状态:</ion-label>
 *     <ion-select [(ngModel)]="lotteryState" (ngModelChange)="changParam()"
 *                 cancelText="取消" okText="确定">
 *       <ion-select-option value="-1 ">不限</ion-select-option>
 *       <ion-select-option value="1 ">已结算</ion-select-option>
 *       <ion-select-option value="0 ">未结算</ion-select-option>
 *
 * 点击后弹 ion-alert（radio 型），alert 的 header 取的是同一个 ion-item 里
 * ion-label 的文本，所以标题是「结算状态:」。这里用 alertHeader 显式传入。
 */
import { computed, ref } from 'vue'
import LegacyAlert from './LegacyAlert.vue'

const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  // [{ value, label }]，对应 ion-select-option
  options: { type: Array, default: () => [] },
  // 对应 alert 的 header，旧版是自动从 ion-label 取的
  alertHeader: { type: String, default: '' },
  cancelText: { type: String, default: '取消' },
  okText: { type: String, default: '确定' },
  placeholder: { type: String, default: '' },
  wrapLabels: { type: Boolean, default: false },
  // 旧版 <ion-select [disabled]="...">，禁用时 :host(.select-disabled){opacity:.4;pointer-events:none}
  disabled: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue', 'change'])

const open = ref(false)

// 旧版取显示文本的逻辑：在 options 里按 value 找，找不到就用 placeholder 并加 .select-placeholder
const matched = computed(() => props.options.find((option) => option.value === props.modelValue))
const isPlaceholder = computed(() => !matched.value)
const selectText = computed(() => (matched.value ? matched.value.label : props.placeholder))

function onConfirm(value) {
  open.value = false
  if (value === props.modelValue) return
  emit('update:modelValue', value)
  // 旧版 ngModelChange 会同时触发 changParam()
  emit('change', value)
}
</script>

<template>
  <div class="legacy-select in-item" :class="{ 'select-disabled': disabled, 'wrap-labels': wrapLabels }">
    <div class="select-text" :class="{ 'select-placeholder': isPlaceholder }">{{ selectText }}</div>
    <div class="select-icon" role="presentation"><div class="select-icon-inner"></div></div>
    <!-- 旧版是一个铺满整个 select 的透明 button 作为点击区 -->
    <button type="button" :aria-label="alertHeader" :disabled="disabled" @click="open = true"></button>

    <!-- 旧版 alert 是挂在 <ion-app> 顶层的 overlay。这里必须 Teleport 到 body：
         滚动容器 .refresher-scroll 上有 transform，会把 position:fixed 的定位基准
         变成那个容器，alert 就会被限制在列表里并跟着滚。 -->
    <Teleport to="body">
      <LegacyAlert
        v-if="open"
        :header="alertHeader"
        :inputs="options"
        :wrap-labels="wrapLabels"
        :value="modelValue"
        :cancel-text="cancelText"
        :ok-text="okText"
        @cancel="open = false"
        @confirm="onConfirm"
      />
    </Teleport>
  </div>
</template>

<style scoped>
/* --- 逐条照抄 web/133 的 md 样式，.sc-ion-select-md-h → .legacy-select --- */
.legacy-select{
  --padding-top:10px;--padding-end:0;--padding-bottom:11px;--padding-start:16px;
  padding-left:var(--padding-start);padding-right:var(--padding-end);
  padding-top:var(--padding-top);padding-bottom:var(--padding-bottom);
  display:flex;position:relative;overflow:hidden;z-index:2;
}
/* 旧版 :host(.in-item){position:static;max-width:45%} */
.legacy-select.in-item{position:static;max-width:45%}
/* 旧版 :host(.select-disabled){opacity:.4;pointer-events:none} */
.legacy-select.select-disabled{opacity:.4;pointer-events:none}
.select-text{flex:1;min-width:16px;font-size:inherit;text-overflow:ellipsis;white-space:nowrap;overflow:hidden}
.legacy-select.wrap-labels{max-width:calc(100% - 64px)}
.wrap-labels .select-text{white-space:normal;overflow-wrap:anywhere;line-height:1.4}
.wrap-labels .select-icon{flex-shrink:0}
.select-placeholder{color:currentColor;opacity:.33}
.select-icon{position:relative;width:19px;height:19px}
/* 那个小三角就是纯 CSS 边框画的 */
.select-icon-inner{
  left:5px;top:50%;margin-top:-3px;position:absolute;width:0;height:0;
  border-top:5px solid;border-right:5px solid transparent;border-left:5px solid transparent;
  color:currentColor;opacity:.33;pointer-events:none;
}
.legacy-select > button{
  left:0;top:0;margin:0;position:absolute;width:100%;height:100%;
  border:0;background:transparent;cursor:pointer;appearance:none;outline:none;
}
</style>
